import React, { useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { LevelBadge } from '../../ui/LevelBadge';
import { ProcessingTag } from '../../ui/ProcessingTag';
import { WhyPopover } from '../../ui/WhyPopover';
import { Feedback } from './Feedback';
import { getAgent } from '../../../utils/agents';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import type { ActionLevel, AgentId, Processing } from '../../../types/anna';
interface WallCardProps {
  agent: AgentId;
  kicker?: string;
  title: string;
  level?: ActionLevel;
  where: Processing;
  traceId: string;
  feedback?: boolean;
  /** Extra actions revealed with the details (e.g. Aanpassen / Afwijzen) */
  actions?: React.ReactNode;
  size?: 'orbit' | 'focus';
  /** Solid surface instead of glass (front card of the approval stack) */
  opaque?: boolean;
  children?: React.ReactNode;
}
const LONG_PRESS_MS = 450;
const EASE = [0.23, 1, 0.32, 1] as const;
const isControl = (target: EventTarget | null) => target instanceof Element && !!target.closest('button, a, input, textarea, [role="slider"], [role="dialog"]');

/**
 * Summoned card on the wall (all text ≥ 22 px). Content first; the chrome stays out of the way:
 * one subtle line at the bottom. Tap or long-press reveals level, lokaal/cloud and "Waarom?".
 */
export function WallCard({
  agent,
  kicker,
  title,
  level,
  where,
  traceId,
  feedback = false,
  actions,
  size = 'orbit',
  opaque = false,
  children
}: WallCardProps) {
  const a = getAgent(agent);
  const reduce = useMotionPreference();
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const detailsId = useId();
  const focus = size === 'focus';
  const clear = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  return <article className={`w-full rounded-[28px] text-left ${opaque ? 'border border-white/10 bg-night-800' : 'glass'} ${focus ? 'p-7 xl:p-8' : 'p-6'}`} onPointerDown={e => {
    if (isControl(e.target)) return;
    longPressed.current = false;
    clear();
    timer.current = window.setTimeout(() => {
      longPressed.current = true;
      setOpen(true);
    }, LONG_PRESS_MS);
  }} onPointerUp={clear} onPointerLeave={clear} onPointerCancel={clear} onClick={e => {
    if (isControl(e.target)) return;
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    setOpen(v => !v);
  }} data-id="element-1587" data-mp="k284nb704ph7">
      <h3 className={`font-semibold leading-tight text-ink ${focus ? 'text-[34px]' : 'text-[28px]'}`} data-id="element-1588" data-mp="2a95z4eapfgrc">{title}</h3>
      {children && <div className={`mt-4 leading-snug text-ink-soft ${focus ? 'text-[24px]' : 'text-[22px]'}`} data-id="element-1589" data-mp="3lmhwc6byl14j">{children}</div>}

      <button type="button" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen(v => !v)} className="mt-5 flex w-full items-center gap-3 rounded-sm border-t border-white/[0.07] pt-4 text-left text-[22px] text-ink-muted transition-colors duration-150 hover:text-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-1590" data-mp="3qk1ma3i0r2cb">
        <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{
        backgroundColor: a.color
      }} data-id="element-1591" data-mp="3mwtmwkpydeux" />
        <span className="min-w-0 flex-1 truncate" data-id="element-1592" data-mp="3hztjkhkhmq7i">
          {a.name}
          {kicker ? ` · ${kicker}` : ''}
        </span>
        <span className="sr-only" data-id="element-1593" data-mp="3d1s03vj5govi">{open ? 'Verberg details' : 'Toon niveau, verwerking en waarom'}</span>
        <ChevronDownIcon aria-hidden className={`h-5 w-5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-1594" data-mp="33yqvy0duh7uh" />
      </button>

      <AnimatePresence initial={false} data-id="element-1595" data-mp="167o93mcy1gtn">
        {open && <motion.div id={detailsId} key="details" initial={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} animate={reduce ? {
        opacity: 1
      } : {
        height: 'auto',
        opacity: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="overflow-hidden" data-id="element-1596" data-mp="127nkj8w4rp8v">
            <div className="flex flex-wrap items-center gap-2 pt-4" data-id="element-1597" data-mp="14ay8oq4e9sev">
              {level && <LevelBadge level={level} size="wall" data-id="element-1598" data-mp="qk2wb3uost6j" />}
              <ProcessingTag where={where} size="wall" data-id="element-1599" data-mp="qjb2nyknh5iw" />
              <span className="ml-auto flex items-center gap-1" data-id="element-1600" data-mp="vajcgvpw6swl">
                {feedback && <Feedback data-id="element-1601" data-mp="vcmcm8kre2f1" />}
                <WhyPopover traceId={traceId} size="wall" data-id="element-1602" data-mp="gmez879lz9l7" />
              </span>
            </div>
            {actions && <div className="mt-3 flex flex-wrap gap-2" data-id="element-1603" data-mp="lk88j967kjpp">{actions}</div>}
          </motion.div>}
      </AnimatePresence>
    </article>;
}